import React, { useEffect, useMemo, useState } from 'react';
import {
    ShieldAlert, Loader2, CheckCircle2, XCircle, Image as ImageIcon,
    Clock, User, RefreshCw, FileText, Briefcase, ChevronDown, ChevronRight,
    X, Download, PlayCircle,
} from 'lucide-react';
import { IncaricoService } from '../services/apiService';
import { PendingApprovalItem } from '../types';
import { useToast } from '../components/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { emitApprovazioniChanged, subscribeApprovazioniRefreshRequested } from '../utils/approvazioniEvents';

const ReasonBadge = ({ reason }: { reason: 'OUTSIDE_RANGE' | 'DUPLICATE' | 'DELETION_REQUEST' | 'MANUAL_DATE' | string }) => {
    if (reason === 'OUTSIDE_RANGE') {
        return (
            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                Fuori periodo
            </span>
        );
    }
    // Richiesta di eliminazione da parte del collaboratore.
    if (reason === 'DELETION_REQUEST') {
        return (
            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                Richiesta eliminazione
            </span>
        );
    }
    // Data proposta dal collaboratore per un media senza EXIF: va approvata
    // esplicitamente dal titolare.
    if (reason === 'MANUAL_DATE') {
        return (
            <span
                title="Il collaboratore ha proposto una data per un media senza EXIF. Approvare significa accettare la data come autodichiarazione."
                className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"
            >
                Data manuale
            </span>
        );
    }
    // Tooltip sul criterio usato per i duplicati (hash SHA-256).
    return (
        <span
            title="Criterio: hash binario SHA-256 (file identici al bit, indipendentemente da nome o categoria)"
            className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full bg-rose-100 text-rose-700"
        >
            Duplicato
        </span>
    );
};

interface PraticaGroup {
    praticaId: number | null;
    praticaName: string;
    items: PendingApprovalItem[];
}

export const PendingApprovalsView: React.FC = () => {
    const { addToast } = useToast();
    const { currentSedeId } = useAuth();

    const [items, setItems] = useState<PendingApprovalItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
    const [confirmRejectId, setConfirmRejectId] = useState<number | null>(null);
    // Lightbox al click sulla thumbnail: mostra la thumbnailUrl ingrandita (già
    // presigned); per la risoluzione piena c'è "Apri originale".
    const [previewItem, setPreviewItem] = useState<PendingApprovalItem | null>(null);
    // Per i video la presigned della thumbnail può essere scaduta o avere problemi
    // di range/CORS: chiediamo un URL fresco con getMediaUrl, come in TabMedia.
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    // Per gruppo: id pratica → conferma rifiuto in attesa / azione in corso
    const [bulkLoadingGroup, setBulkLoadingGroup] = useState<number | string | null>(null);
    const [bulkConfirmRejectGroup, setBulkConfirmRejectGroup] = useState<number | string | null>(null);
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!previewItem) {
            setPreviewUrl(null);
            setIsLoadingPreview(false);
            return;
        }
        let mounted = true;
        setIsLoadingPreview(true);
        setPreviewUrl(previewItem.thumbnailUrl || null);
        if (previewItem.mediaId) {
            IncaricoService.getMediaUrl(previewItem.mediaId)
                .then((res: any) => {
                    const url = typeof res === 'string' ? res : (res?.url || res?.data?.url || res?.data);
                    if (mounted && typeof url === 'string' && url) setPreviewUrl(url);
                })
                .catch(() => { /* In caso di errore resta la thumbnailUrl */ })
                .finally(() => { if (mounted) setIsLoadingPreview(false); });
        } else {
            setIsLoadingPreview(false);
        }
        return () => { mounted = false; };
    }, [previewItem]);

    const loadItems = async () => {
        setLoading(true);
        try {
            const sedeId = typeof currentSedeId === 'number' ? currentSedeId : undefined;
            const data = await IncaricoService.getPendingApprovals(sedeId);
            setItems(Array.isArray(data) ? data : []);
        } catch {
            addToast('Errore nel caricamento delle approvazioni', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadItems();
    }, [currentSedeId]);

    // Ricarica quando il container /approvazioni chiede un aggiornamento.
    useEffect(() => subscribeApprovazioniRefreshRequested(() => { loadItems(); }), [currentSedeId]);

    // Raggruppa per mandato; gli elementi senza praticaId finiscono in "Senza mandato".
    const groups = useMemo<PraticaGroup[]>(() => {
        const map = new Map<string, PraticaGroup>();
        for (const item of items) {
            const key = item.praticaId != null ? `p_${item.praticaId}` : 'no_pratica';
            const existing = map.get(key);
            if (existing) {
                existing.items.push(item);
            } else {
                map.set(key, {
                    praticaId: item.praticaId ?? null,
                    praticaName: item.praticaName || (item.praticaId ? `Mandato #${item.praticaId}` : 'Senza mandato'),
                    items: [item],
                });
            }
        }
        return Array.from(map.values()).sort((a, b) =>
            a.praticaName.localeCompare(b.praticaName, 'it', { sensitivity: 'base' })
        );
    }, [items]);

    const groupKey = (g: PraticaGroup) => g.praticaId != null ? `p_${g.praticaId}` : 'no_pratica';

    const toggleGroup = (key: string) => {
        setCollapsedGroups(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const handleApprove = async (id: number) => {
        setActionLoadingId(id);
        try {
            await IncaricoService.approvePendingApproval(id);
            // "Approva" ha effetti diversi in base al motivo:
            // OUTSIDE_RANGE / DUPLICATE → watermark sul file, che resta
            // DELETION_REQUEST → il file viene eliminato
            // MANUAL_DATE → la data proposta viene scritta sul media
            // Il toast descrive l'esito specifico.
            const item = items.find(i => i.id === id);
            const reason = (item as any)?.reason;
            setItems(prev => prev.filter(i => i.id !== id));
            const msg = reason === 'DELETION_REQUEST'
                ? 'Eliminazione confermata'
                : reason === 'MANUAL_DATE'
                    ? 'Data approvata e scritta sul media'
                    : 'Approvato, watermark in elaborazione';
            addToast(msg, 'success');
            emitApprovazioniChanged();
        } catch {
            addToast('Errore durante l\'approvazione', 'error');
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleReject = async (id: number) => {
        setActionLoadingId(id);
        setConfirmRejectId(null);
        try {
            const rejected = items.find(i => i.id === id);
            await IncaricoService.rejectPendingApproval(id);
            setItems(prev => prev.filter(i => i.id !== id));
            // Il rifiuto elimina il file, tranne per le richieste di eliminazione
            // (rifiutare significa tenerlo).
            addToast(rejected?.reason === 'DELETION_REQUEST' ? 'Richiesta rifiutata: il file resta' : 'File rifiutato ed eliminato', 'info');
            emitApprovazioniChanged();
        } catch {
            addToast('Errore durante il rifiuto', 'error');
        } finally {
            setActionLoadingId(null);
        }
    };

    // Azione massiva su tutti i pending del mandato. Con troppe chiamate in parallelo
    // il server si saturava e alcune andavano in timeout lato client pur essendo state
    // elaborate. Usiamo 5 worker e a fine batch ricarichiamo i pending: quelli spariti
    // sono andati a buon fine, così il conteggio è affidabile.
    const runBulkPool = async (
        group: PraticaGroup,
        op: (id: number) => Promise<any>,
    ): Promise<{ okCount: number; failures: number }> => {
        const ids = group.items.map(i => i.id);
        const okIds = new Set<number>();
        let next = 0;
        const CONC = 5;
        const worker = async () => {
            while (next < ids.length) {
                const id = ids[next++];
                try { await op(id); okIds.add(id); } catch { /* Riconciliato con il reload sotto */ }
            }
        };
        await Promise.all(Array.from({ length: Math.min(CONC, ids.length) }, () => worker()));

        let reloaded = false;
        let serverList: any[] = [];
        try {
            const sedeId = typeof currentSedeId === 'number' ? currentSedeId : undefined;
            serverList = await IncaricoService.getPendingApprovals(sedeId);
            setItems(Array.isArray(serverList) ? serverList : []);
            reloaded = true;
        } catch {
            setItems(prev => prev.filter(i => !okIds.has(i.id)));
        }
        let failures: number;
        if (reloaded) {
            const stillPending = new Set(serverList.map((i: any) => i.id));
            failures = ids.filter(id => stillPending.has(id)).length;
        } else {
            failures = ids.length - okIds.size;
        }
        return { okCount: ids.length - failures, failures };
    };

    const handleBulkApprove = async (group: PraticaGroup) => {
        const key = groupKey(group);
        setBulkLoadingGroup(key);
        const { okCount, failures } = await runBulkPool(group, id => IncaricoService.approvePendingApproval(id));
        if (failures === 0) {
            addToast(`${okCount} file approvati per ${group.praticaName}`, 'success');
        } else {
            addToast(`${okCount} approvati, ${failures} non riusciti — riprova sui rimasti`, okCount === 0 ? 'error' : 'info');
        }
        emitApprovazioniChanged();
        setBulkLoadingGroup(null);
    };

    const handleBulkReject = async (group: PraticaGroup) => {
        const key = groupKey(group);
        setBulkLoadingGroup(key);
        setBulkConfirmRejectGroup(null);
        const { okCount, failures } = await runBulkPool(group, id => IncaricoService.rejectPendingApproval(id));
        if (failures === 0) {
            addToast(`${okCount} file rifiutati per ${group.praticaName}`, 'info');
        } else {
            addToast(`${okCount} rifiutati, ${failures} non riusciti — riprova sui rimasti`, okCount === 0 ? 'error' : 'info');
        }
        emitApprovazioniChanged();
        setBulkLoadingGroup(null);
    };

    return (
        <div className="p-6 max-w-5xl mx-auto space-y-6">
            {/* Header */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="bg-rose-600 p-2.5 rounded-xl text-white">
                        <ShieldAlert size={20} />
                    </div>
                    <div>
                        <h1 className="text-lg font-black text-slate-900 uppercase tracking-wide">Approvazioni Media</h1>
                        <p className="text-xs text-slate-500">
                            {loading
                                ? 'Caricamento...'
                                : `${items.length} file · ${groups.length} ${groups.length === 1 ? 'mandato' : 'mandati'}`}
                        </p>
                    </div>
                </div>
                <button
                    onClick={loadItems}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                    <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
                    Aggiorna
                </button>
            </div>

            {/* Content */}
            {loading ? (
                <div className="flex items-center justify-center py-24 text-blue-500 gap-3">
                    <Loader2 className="animate-spin" size={36} />
                    <span className="font-bold text-slate-500">Caricamento...</span>
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl py-20 text-center">
                    <CheckCircle2 size={48} className="mx-auto mb-4 text-emerald-300" />
                    <p className="font-black text-slate-500 text-base">Nessun file in attesa di approvazione</p>
                    <p className="text-xs text-slate-400 mt-1">Tutti i file sono stati elaborati</p>
                </div>
            ) : (
                <div className="space-y-5">
                    {groups.map(group => {
                        const key = groupKey(group);
                        const isCollapsed = collapsedGroups.has(key);
                        const isBulkLoading = bulkLoadingGroup === key;
                        const isBulkConfirm = bulkConfirmRejectGroup === key;
                        return (
                            <div key={key} className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                                {/* Header del gruppo mandato */}
                                <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 flex items-center justify-between gap-3">
                                    <button
                                        onClick={() => toggleGroup(key)}
                                        className="flex items-center gap-3 min-w-0 flex-1 text-left"
                                    >
                                        {isCollapsed
                                            ? <ChevronRight size={18} className="text-slate-400 shrink-0" />
                                            : <ChevronDown size={18} className="text-slate-400 shrink-0" />}
                                        <div className="bg-indigo-100 p-2 rounded-lg shrink-0">
                                            <Briefcase size={16} className="text-indigo-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm font-black text-slate-900 truncate uppercase tracking-tight">
                                                {group.praticaName}
                                            </p>
                                            <p className="text-[11px] text-slate-500 font-medium">
                                                {group.items.length} file in attesa
                                            </p>
                                        </div>
                                    </button>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {isBulkConfirm ? (
                                            <>
                                                <span className="text-xs text-slate-500 font-medium">Rifiutare tutti? I file inviati in approvazione verranno ELIMINATI.</span>
                                                <button
                                                    onClick={() => setBulkConfirmRejectGroup(null)}
                                                    className="px-3 py-1.5 text-xs font-bold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                                                >
                                                    Annulla
                                                </button>
                                                <button
                                                    onClick={() => handleBulkReject(group)}
                                                    disabled={isBulkLoading}
                                                    className="px-3 py-1.5 text-xs font-black text-white bg-rose-600 hover:bg-rose-700 rounded-lg flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                                                >
                                                    {isBulkLoading
                                                        ? <Loader2 size={13} className="animate-spin" />
                                                        : <XCircle size={13} />}
                                                    Rifiuta tutti ed elimina
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => setBulkConfirmRejectGroup(key)}
                                                    disabled={isBulkLoading}
                                                    className="px-3 py-1.5 text-xs font-bold text-rose-600 border border-rose-200 hover:bg-rose-50 rounded-lg flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                                                >
                                                    <XCircle size={14} /> Rifiuta tutti
                                                </button>
                                                <button
                                                    onClick={() => handleBulkApprove(group)}
                                                    disabled={isBulkLoading}
                                                    className="px-4 py-1.5 text-xs font-black text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg flex items-center gap-1.5 disabled:opacity-50 shadow-sm transition-colors"
                                                >
                                                    {isBulkLoading
                                                        ? <Loader2 size={13} className="animate-spin" />
                                                        : <CheckCircle2 size={13} />}
                                                    Approva tutti
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Items del mandato */}
                                {!isCollapsed && (
                                    <div className="divide-y divide-slate-100">
                                        {group.items.map(item => (
                                            <div
                                                key={item.id}
                                                className="flex flex-col sm:flex-row items-stretch"
                                            >
                                                {/*
                                                  Thumbnail cliccabile per la lightbox. Per i video la thumbnailUrl è il .mp4
                                                  compresso: usiamo <video> e ci spostiamo a 0.1s per mostrare il primo frame.
                                                  pointer-events-none fa arrivare il click al wrapper.
                                                */}
                                                {(() => {
                                                    const isVideoThumb = /\.(mp4|mov|webm|m4v|avi|mts|m2ts|mkv|mpg|mpeg)(\?|$)/i.test(item.mediaName || '');
                                                    return (
                                                        <div
                                                            role={item.thumbnailUrl ? 'button' : undefined}
                                                            tabIndex={item.thumbnailUrl ? 0 : -1}
                                                            onClick={() => item.thumbnailUrl && setPreviewItem(item)}
                                                            onKeyDown={(e) => {
                                                                if (item.thumbnailUrl && (e.key === 'Enter' || e.key === ' ')) {
                                                                    e.preventDefault();
                                                                    setPreviewItem(item);
                                                                }
                                                            }}
                                                            title={item.thumbnailUrl ? 'Apri anteprima' : ''}
                                                            className={`relative w-full sm:w-24 h-24 bg-slate-100 flex items-center justify-center shrink-0 overflow-hidden ${
                                                                item.thumbnailUrl ? 'cursor-pointer hover:opacity-90 transition-opacity' : 'cursor-default'
                                                            }`}
                                                        >
                                                            {item.thumbnailUrl ? (
                                                                isVideoThumb ? (
                                                                    <>
                                                                        <video
                                                                            src={item.thumbnailUrl}
                                                                            preload="metadata"
                                                                            muted
                                                                            playsInline
                                                                            onLoadedMetadata={(e) => {
                                                                                // Alcuni browser non disegnano il frame a t=0, serve un seek > 0.
                                                                                try { e.currentTarget.currentTime = 0.1; } catch {}
                                                                            }}
                                                                            className="w-full h-full object-cover pointer-events-none"
                                                                        />
                                                                        <PlayCircle size={28} className="absolute text-white drop-shadow-lg pointer-events-none" />
                                                                    </>
                                                                ) : (
                                                                    <img
                                                                        src={item.thumbnailUrl}
                                                                        alt={item.mediaName}
                                                                        className="w-full h-full object-cover pointer-events-none"
                                                                    />
                                                                )
                                                            ) : (
                                                                <ImageIcon size={28} className="text-slate-300" />
                                                            )}
                                                        </div>
                                                    );
                                                })()}

                                                {/* Info */}
                                                <div className="flex-1 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                            <p className="text-sm font-black text-slate-900 truncate">{item.mediaName}</p>
                                                            <ReasonBadge reason={item.reason} />
                                                        </div>
                                                        <div className="flex flex-wrap gap-3 text-[11px] text-slate-500 font-medium">
                                                            <span className="flex items-center gap-1">
                                                                <User size={11} /> {item.submittedByName}
                                                            </span>
                                                            <span className="flex items-center gap-1">
                                                                <FileText size={11} /> {item.incaricoRef}
                                                            </span>
                                                            <span className="flex items-center gap-1">
                                                                <Clock size={11} /> {new Date(item.submittedAt).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        </div>
                                                        {/*
                                                          Per OUTSIDE_RANGE mostriamo data EXIF e periodo pianificato, come vede il
                                                          collaboratore, così il titolare decide con le informazioni. Il badge indica
                                                          se la data viene da EXIF o è stata inserita a mano.
                                                        */}
                                                        {item.reason === 'MANUAL_DATE' && item.proposedDate && (
                            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                                <span className="font-bold text-amber-800">
                                    Data proposta: {new Date(item.proposedDate).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </span>
                                <span className="text-amber-700">
                                    Approvando, la data verrà scritta sul media senza watermark (autodichiarazione, EXIF assente).
                                </span>
                            </div>
                        )}
                        {item.reason === 'OUTSIDE_RANGE' && (item.dataAcquisizione || item.incaricoDataInizio) && (
                                                            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] bg-orange-50 border border-orange-200 rounded-lg px-2.5 py-1.5">
                                                                <span className="font-bold text-orange-800 flex items-center gap-1.5">
                                                                    Data foto: {item.dataAcquisizione ? new Date(item.dataAcquisizione).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                                                                    {item.dataAcquisizioneFromExif === true && (
                                                                        <span className="text-[9px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-300 px-1.5 py-0.5 rounded"
                                                                              title="Data letta dai metadati EXIF della fotocamera (autoritativa)">
                                                                            EXIF
                                                                        </span>
                                                                    )}
                                                                    {item.dataAcquisizioneFromExif === false && (
                                                                        <span className="text-[9px] font-black uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-300 px-1.5 py-0.5 rounded"
                                                                              title="Data digitata manualmente dal collaboratore (autodichiarazione, EXIF assente)">
                                                                            MANUALE
                                                                        </span>
                                                                    )}
                                                                </span>
                                                                <span className="text-orange-700">
                                                                    Periodo incarico: {item.incaricoDataInizio ? new Date(item.incaricoDataInizio).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                                                                    {' → '}
                                                                    {item.incaricoDataFine ? new Date(item.incaricoDataFine).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Actions singolo */}
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        {confirmRejectId === item.id ? (
                                                            <>
                                                                {/* Il rifiuto elimina il file, tranne per le richieste di eliminazione. */}
                                                                <span className="text-xs text-slate-500 font-medium">
                                                                    {item.reason === 'DELETION_REQUEST' ? 'Rifiutare? Il file viene mantenuto.' : 'Rifiutare ed ELIMINARE il file?'}
                                                                </span>
                                                                <button
                                                                    onClick={() => setConfirmRejectId(null)}
                                                                    className="px-3 py-1.5 text-xs font-bold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                                                                >
                                                                    Annulla
                                                                </button>
                                                                <button
                                                                    onClick={() => handleReject(item.id)}
                                                                    disabled={actionLoadingId === item.id}
                                                                    className="px-3 py-1.5 text-xs font-black text-white bg-rose-600 hover:bg-rose-700 rounded-lg flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                                                                >
                                                                    {actionLoadingId === item.id
                                                                        ? <Loader2 size={13} className="animate-spin" />
                                                                        : <XCircle size={13} />}
                                                                    {item.reason === 'DELETION_REQUEST' ? 'Rifiuta (mantieni)' : 'Rifiuta ed elimina'}
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    onClick={() => setConfirmRejectId(item.id)}
                                                                    disabled={actionLoadingId === item.id || isBulkLoading}
                                                                    className="px-3 py-1.5 text-xs font-bold text-rose-600 border border-rose-200 hover:bg-rose-50 rounded-lg flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                                                                >
                                                                    <XCircle size={14} /> Rifiuta
                                                                </button>
                                                                <button
                                                                    onClick={() => handleApprove(item.id)}
                                                                    disabled={actionLoadingId === item.id || isBulkLoading}
                                                                    className="px-4 py-1.5 text-xs font-black text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg flex items-center gap-1.5 disabled:opacity-50 shadow-sm transition-colors"
                                                                >
                                                                    {actionLoadingId === item.id
                                                                        ? <Loader2 size={13} className="animate-spin" />
                                                                        : <CheckCircle2 size={13} />}
                                                                    Approva
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Lightbox anteprima media: chiusura con click esterno o X. */}
            {previewItem && (
                <div
                    className="fixed inset-0 z-[300] bg-slate-900/95 flex flex-col items-center justify-center p-4"
                    onClick={() => setPreviewItem(null)}
                >
                    <button
                        onClick={(e) => { e.stopPropagation(); setPreviewItem(null); }}
                        className="absolute top-5 right-5 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
                        title="Chiudi"
                    >
                        <X size={24} />
                    </button>

                    <div
                        className="w-full max-w-3xl flex flex-col items-center gap-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {(() => {
                            const url = previewUrl || previewItem.thumbnailUrl || '';
                            const isVideo = /\.(mp4|mov|webm|m4v|avi|mts|m2ts|mkv|mpg|mpeg)(\?|$)/i.test(previewItem.mediaName || '');
                            if (isLoadingPreview && !url) {
                                return (
                                    <div className="w-full max-w-3xl h-64 flex items-center justify-center bg-slate-800/40 rounded-2xl">
                                        <Loader2 className="animate-spin text-white" size={40} />
                                    </div>
                                );
                            }
                            if (isVideo) {
                                return (
                                    <div className="w-full bg-black rounded-2xl shadow-2xl flex items-center justify-center max-h-[70vh]">
                                        <video
                                            key={url}
                                            src={url}
                                            controls
                                            autoPlay
                                            playsInline
                                            className="max-w-full max-h-[70vh] rounded-2xl"
                                        />
                                    </div>
                                );
                            }
                            return (
                                <img
                                    src={url}
                                    alt={previewItem.mediaName}
                                    className="max-w-full max-h-[70vh] rounded-2xl object-contain shadow-2xl"
                                />
                            );
                        })()}

                        {/* Info file + pulsanti */}
                        <div className="w-full bg-slate-800/80 backdrop-blur-sm rounded-2xl p-4 border border-slate-700">
                            <p className="text-sm font-bold text-white truncate">{previewItem.mediaName}</p>
                            <p className="text-[10px] text-slate-400 mt-1">
                                {previewItem.submittedByName} · {previewItem.incaricoRef}
                            </p>
                        </div>

                        {(previewUrl || previewItem.thumbnailUrl) && (
                            <a
                                href={previewUrl || previewItem.thumbnailUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="w-full max-w-md py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black text-xs uppercase text-center flex items-center justify-center gap-2 active:scale-95 transition-transform"
                            >
                                <Download size={14}/> Apri originale in nuova tab
                            </a>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PendingApprovalsView;
